import { ArrowLeft, LogOut, Plus, Sparkles, Users } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { CircleChat } from "@/components/karma/CircleChat";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";

import { getAutoCircleRituals } from "@/lib/karma/circle-content";
import { computeKarmaNumerology } from "@/lib/karma/numerology";
import type { KarmaCircle, KarmaCircleRule, KarmaCircleRuleKey, KarmaCircleRuleSet } from "@/lib/karma/types";
import {
  addCircleRitual,
  createCircle,
  ensureStore,
  getMe,
  getMyCircle,
  joinCircle,
  leaveMyCircle,
  listCircleMembers,
  listEligibleCirclesForUser,
} from "@/lib/karma/storage";

function initials(name: string) {
  const parts = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2);

  const letters = parts.map((p) => p[0]?.toLocaleUpperCase("tr-TR") ?? "").join("");
  return letters || "?";
}

function keyLabel(key: KarmaCircleRuleKey) {
  switch (key) {
    case "destinyNumber":
      return "Kader";
    case "heartDesireNumber":
      return "Kalp arzusu";
    case "expressionNumber":
      return "İfade";
    case "personalityNumber":
      return "Kişilik";
    case "birthDayNumber":
      return "Doğum günü";
    case "karmicDebt":
      return "Karmik borç";
    default:
      return key;
  }
}

function rulesLabel(rules?: KarmaCircleRuleSet) {
  const all = rules?.all ?? [];
  if (!all.length) return "Genel";
  return all.map((r) => `${keyLabel(r.key)}=${r.value}`).join(" • ");
}

function parseSelectNumber(value: string) {
  const n = Math.trunc(Number(value));
  if (!Number.isFinite(n)) return null;
  if (n <= 0) return null;
  return n;
}

function NumberSelect(props: {
  value: number | null;
  onValueChange: (n: number | null) => void;
  placeholder: string;
  options: number[];
}) {
  const { value, onValueChange, placeholder, options } = props;

  return (
    <Select
      value={value ? String(value) : "_"}
      onValueChange={(v) => {
        if (v === "_") onValueChange(null);
        else onValueChange(parseSelectNumber(v));
      }}
    >
      <SelectTrigger>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="_">Seçme</SelectItem>
        {options.map((n) => (
          <SelectItem key={n} value={String(n)}>
            {n}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function CircleCard(props: {
  circle: KarmaCircle;
  actionLabel: string;
  onAction: () => void;
  disabled?: boolean;
}) {
  const { circle, actionLabel, onAction, disabled } = props;

  return (
    <div className="rounded-2xl border bg-muted/30 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2 text-sm font-semibold">
            <Users className="h-4 w-4" />
            <span className="truncate">{circle.name}</span>
          </div>
          <div className="mt-1 text-sm text-muted-foreground">
            {circle.memberUserIds.length} kişi • {rulesLabel(circle.rules)}
          </div>
        </div>

        <Button
          className="rounded-full"
          variant="secondary"
          onClick={onAction}
          disabled={disabled}
        >
          {actionLabel}
        </Button>
      </div>
    </div>
  );
}

export default function KarmaCember() {
  const [storeVersion, setStoreVersion] = useState(0);

  const [newName, setNewName] = useState("");
  const [ruleDestiny, setRuleDestiny] = useState<number | null>(null);
  const [ruleHeart, setRuleHeart] = useState<number | null>(null);
  const [ruleExpression, setRuleExpression] = useState<number | null>(null);
  const [rulePersonality, setRulePersonality] = useState<number | null>(null);
  const [ruleBirthDay, setRuleBirthDay] = useState<number | null>(null);
  const [ruleKarmicDebts, setRuleKarmicDebts] = useState<number[]>([]);

  const [ritualTitle, setRitualTitle] = useState("");
  const [ritualDescription, setRitualDescription] = useState("");

  const store = useMemo(() => {
    storeVersion;
    return ensureStore();
  }, [storeVersion]);

  const me = getMe(store);
  const myCircle = getMyCircle(store);
  const members = myCircle ? listCircleMembers(myCircle.id, store) : [];

  const meNumerology = useMemo(
    () => computeKarmaNumerology(me.displayName, me.birthDate),
    [me.displayName, me.birthDate],
  );

  const eligibleCircles = useMemo(() => listEligibleCirclesForUser(me.id, store), [me.id, store]);
  const otherCircles = useMemo(() => {
    const currentId = myCircle?.id;
    return eligibleCircles
      .filter((c) => c.id !== currentId)
      .filter((c) => c.memberUserIds.length > 0)
      .slice(0, 40);
  }, [eligibleCircles, myCircle?.id]);

  const newRules: KarmaCircleRule[] = [
    ruleDestiny ? ({ key: "destinyNumber", value: ruleDestiny } satisfies KarmaCircleRule) : null,
    ruleHeart ? ({ key: "heartDesireNumber", value: ruleHeart } satisfies KarmaCircleRule) : null,
    ruleExpression
      ? ({ key: "expressionNumber", value: ruleExpression } satisfies KarmaCircleRule)
      : null,
    rulePersonality
      ? ({ key: "personalityNumber", value: rulePersonality } satisfies KarmaCircleRule)
      : null,
    ruleBirthDay
      ? ({ key: "birthDayNumber", value: ruleBirthDay } satisfies KarmaCircleRule)
      : null,
    ...ruleKarmicDebts.map((v) => ({ key: "karmicDebt", value: v } satisfies KarmaCircleRule)),
  ].filter(Boolean) as KarmaCircleRule[];

  const canCreate = newName.trim().length >= 2 && newRules.length >= 1;

  const autoRituals = myCircle
    ? getAutoCircleRituals({ circle: myCircle, meNumerology, now: new Date() })
    : [];
  const manualRituals = myCircle?.rituals ?? [];

  const isOwner = Boolean(myCircle && myCircle.ownerUserId === me.id);

  return (
    <div className="w-full space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Çember</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Çemberler numeroloji kriterlerine göre filtrelenir; isteyen katılır.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button asChild variant="outline" className="rounded-full">
            <Link to="/">
              <ArrowLeft className="mr-2 h-4 w-4" /> Koç/Keşif
            </Link>
          </Button>
          <Button asChild className="rounded-full">
            <Link to="/karma/me">Panel</Link>
          </Button>
        </div>
      </div>

      <Card className="p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="font-display text-lg font-semibold tracking-tight">Benim çemberim</div>
            <div className="mt-1 text-sm text-muted-foreground">
              {myCircle
                ? `${myCircle.name} • ${members.length} kişi • ${rulesLabel(myCircle.rules)}`
                : "Henüz bir çembere dahil değilsin."}
            </div>
          </div>

          {myCircle ? (
            <Button
              variant="outline"
              className="rounded-full"
              onClick={() => {
                leaveMyCircle();
                setStoreVersion((v) => v + 1);
              }}
            >
              <LogOut className="mr-2 h-4 w-4" /> Ayrıl
            </Button>
          ) : null}
        </div>

        {myCircle ? (
          <>
            <Separator className="my-4" />

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {members.map((u) => {
                const photo = u.photos?.[0]?.url ?? "";
                return (
                  <div key={u.id} className="flex items-center gap-3 rounded-2xl border bg-muted/30 p-3">
                    <Avatar className="h-10 w-10">
                      {photo ? <AvatarImage src={photo} alt={u.displayName} /> : null}
                      <AvatarFallback>{initials(u.displayName)}</AvatarFallback>
                    </Avatar>
                    <div className="min-w-0">
                      <div className="truncate text-sm font-semibold">{u.displayName}</div>
                      <div className="text-xs text-muted-foreground">İtibar: {u.reputation ?? 0}</div>
                    </div>
                    {u.id === me.id ? (
                      <span className="ml-auto rounded-full bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                        Sen
                      </span>
                    ) : null}
                  </div>
                );
              })}
            </div>

            <Separator className="my-5" />

            <div className="text-sm font-semibold">Grup sohbeti</div>
            <div className="mt-1 text-sm text-muted-foreground">
              WhatsApp grubu gibi: çember üyeleri burada yazışır.
            </div>
            <div className="mt-4">
              <CircleChat circle={myCircle} me={me} />
            </div>

            <Separator className="my-5" />

            <div className="flex items-center gap-2 text-sm font-semibold">
              <Sparkles className="h-4 w-4" /> Ritüeller
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              Sistem, çember kurallarına göre öneri üretir. Kurucu isterse manuel ekleyebilir.
            </div>

            <div className="mt-4 grid gap-3">
              {[...autoRituals, ...manualRituals].slice(0, 10).map((r) => (
                <div key={r.id} className="rounded-2xl border bg-muted/30 p-4">
                  <div className="text-sm font-semibold">{r.title}</div>
                  <div className="mt-1 text-sm text-muted-foreground">{r.description}</div>
                </div>
              ))}

              {autoRituals.length + manualRituals.length === 0 ? (
                <div className="text-sm text-muted-foreground">Bu çember için ritüel önerisi yok.</div>
              ) : null}
            </div>

            {isOwner ? (
              <>
                <Separator className="my-5" />

                <div className="text-sm font-semibold">Manuel ritüel ekle</div>
                <div className="mt-3 grid gap-3">
                  <div className="grid gap-2">
                    <Label>Başlık</Label>
                    <Input value={ritualTitle} onChange={(e) => setRitualTitle(e.target.value)} />
                  </div>
                  <div className="grid gap-2">
                    <Label>Açıklama</Label>
                    <Textarea
                      value={ritualDescription}
                      onChange={(e) => setRitualDescription(e.target.value)}
                      rows={3}
                    />
                  </div>
                  <div>
                    <Button
                      className="rounded-full"
                      onClick={() => {
                        if (!myCircle) return;
                        addCircleRitual(myCircle.id, ritualTitle, ritualDescription);
                        setRitualTitle("");
                        setRitualDescription("");
                        setStoreVersion((v) => v + 1);
                      }}
                    >
                      <Plus className="mr-2 h-4 w-4" /> Ekle
                    </Button>
                  </div>
                </div>
              </>
            ) : null}
          </>
        ) : null}
      </Card>

      <Card className="p-5">
        <div className="font-display text-lg font-semibold tracking-tight">Yeni çember oluştur</div>
        <div className="mt-1 text-sm text-muted-foreground">
          Çoklu kriter (AND) ile çember daralır ve sadece uygun profillere görünür.
        </div>
        <Separator className="my-4" />

        <div className="grid gap-4">
          <div className="grid gap-2">
            <Label>Çember adı</Label>
            <Input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Örn: Kader 9 + Karmik 4"
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="grid gap-2">
              <Label>Kader sayısı</Label>
              <NumberSelect
                value={ruleDestiny}
                onValueChange={setRuleDestiny}
                placeholder="Seç"
                options={[1, 2, 3, 4, 5, 6, 7, 8, 9]}
              />
            </div>
            <div className="grid gap-2">
              <Label>Kalp arzusu</Label>
              <NumberSelect
                value={ruleHeart}
                onValueChange={setRuleHeart}
                placeholder="Seç"
                options={[1, 2, 3, 4, 5, 6, 7, 8, 9]}
              />
            </div>
            <div className="grid gap-2">
              <Label>İfade (isim)</Label>
              <NumberSelect
                value={ruleExpression}
                onValueChange={setRuleExpression}
                placeholder="Seç"
                options={[1, 2, 3, 4, 5, 6, 7, 8, 9]}
              />
            </div>
            <div className="grid gap-2">
              <Label>Kişilik</Label>
              <NumberSelect
                value={rulePersonality}
                onValueChange={setRulePersonality}
                placeholder="Seç"
                options={[1, 2, 3, 4, 5, 6, 7, 8, 9]}
              />
            </div>
            <div className="grid gap-2">
              <Label>Doğum günü</Label>
              <NumberSelect
                value={ruleBirthDay}
                onValueChange={setRuleBirthDay}
                placeholder="Seç"
                options={[1, 2, 3, 4, 5, 6, 7, 8, 9]}
              />
            </div>
            <div className="grid gap-2">
              <Label>Karmik borç (çoklu)</Label>
              <div className="rounded-2xl border bg-muted/30 p-3">
                <ToggleGroup
                  type="multiple"
                  variant="outline"
                  size="sm"
                  className="flex flex-wrap justify-start gap-2"
                  value={ruleKarmicDebts.map(String)}
                  onValueChange={(vals) => {
                    const next = vals
                      .map((v) => Math.trunc(Number(v)))
                      .filter((n) => Number.isFinite(n) && n >= 1 && n <= 9)
                      .sort((a, b) => a - b);
                    setRuleKarmicDebts(next);
                  }}
                >
                  {([1, 2, 3, 4, 5, 6, 7, 8, 9] as const).map((n) => (
                    <ToggleGroupItem key={n} value={String(n)} className="rounded-full">
                      K{n}
                    </ToggleGroupItem>
                  ))}
                </ToggleGroup>
              </div>
              <div className="text-xs text-muted-foreground">
                Karmik borç için “OR” çalışır: seçtiklerinden en az biri sende varsa uygunsun.
              </div>
            </div>
          </div>

          <div className="rounded-2xl border bg-muted/30 p-4">
            <div className="text-sm font-semibold">Ön izleme</div>
            <div className="mt-1 text-sm text-muted-foreground">
              Bu çember yalnızca şu kurallara uyanlara görünür: {newRules.length ? rulesLabel({ all: newRules }) : "(kural yok)"}
            </div>
          </div>

          <div>
            <Button
              className="rounded-full"
              disabled={!canCreate}
              onClick={() => {
                createCircle({ name: newName, rules: { all: newRules } });
                setNewName("");
                setRuleDestiny(null);
                setRuleHeart(null);
                setRuleExpression(null);
                setRulePersonality(null);
                setRuleBirthDay(null);
                setRuleKarmicDebts([]);
                setStoreVersion((v) => v + 1);
              }}
            >
              <Plus className="mr-2 h-4 w-4" /> Oluştur
            </Button>
            {!canCreate ? (
              <div className="mt-2 text-xs text-muted-foreground">
                Oluşturmak için isim ve en az 1 kriter seç.
              </div>
            ) : null}
          </div>
        </div>
      </Card>

      <Card className="p-5">
        <div className="font-display text-lg font-semibold tracking-tight">Çember keşfet</div>
        <div className="mt-1 text-sm text-muted-foreground">
          Burada sadece senin numerolojine uyan çemberler görünür.
        </div>
        <Separator className="my-4" />

        {otherCircles.length === 0 ? (
          <div className="text-sm text-muted-foreground">Şu an sana uygun çember yok.</div>
        ) : (
          <div className="grid gap-3">
            {otherCircles.map((c) => (
              <CircleCard
                key={c.id}
                circle={c}
                actionLabel="Katıl"
                disabled={c.memberUserIds.length >= 12}
                onAction={() => {
                  joinCircle(c.id);
                  setStoreVersion((v) => v + 1);
                }}
              />
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
